import { cn } from '@/utils/cn'
import { getInitials } from '@/utils/formatters'
import { resolveMediaUrl } from '@/utils/media'
import { useEffect, useState } from 'react'

interface AvatarProps {
  src?: string
  name?: string
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  className?: string
  online?: boolean
}

const sizeClasses = {
  xs: 'w-6 h-6 text-[10px]',
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-16 h-16 text-xl',
}

const onlineDotSizes = {
  xs: 'w-1.5 h-1.5',
  sm: 'w-2 h-2',
  md: 'w-2.5 h-2.5',
  lg: 'w-3 h-3',
  xl: 'w-3.5 h-3.5',
}

const colors = [
  'from-[var(--color-brand)] to-[var(--color-secondary)]',
  'from-[var(--color-brand)] to-sky-600',
  'from-emerald-500 to-teal-600',
  'from-orange-500 to-amber-600',
  'from-pink-500 to-rose-600',
  'from-cyan-500 to-[var(--color-secondary)]',
]

function getColorClass(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash)
  }
  return colors[Math.abs(hash) % colors.length]
}

export function Avatar({
  src,
  name = 'U',
  size = 'md',
  className,
  online,
}: AvatarProps) {
  const [imageFailed, setImageFailed] = useState(false)
  const initials = getInitials(name)
  const colorClass = getColorClass(name)
  const resolvedSrc = resolveMediaUrl(src)

  useEffect(() => {
    setImageFailed(false)
  }, [resolvedSrc])

  return (
    <div className={cn('relative inline-flex shrink-0', className)}>
      {resolvedSrc && !imageFailed ? (
        <img
          src={resolvedSrc}
          alt={name}
          onError={() => setImageFailed(true)}
          className={cn(
            'rounded-full object-cover',
            sizeClasses[size],
          )}
        />
      ) : (
        <div
          className={cn(
            'rounded-full flex items-center justify-center font-semibold text-white',
            `bg-gradient-to-br ${colorClass}`,
            sizeClasses[size],
          )}
          aria-label={name}
        >
          {initials}
        </div>
      )}
      {online !== undefined && (
        <span
          className={cn(
            'absolute bottom-0 right-0 rounded-full border-2 border-[var(--bg-primary)]',
            onlineDotSizes[size],
            online ? 'bg-[var(--color-success)]' : 'bg-[var(--text-muted)]',
          )}
        />
      )}
    </div>
  )
}
